/**
 * `materialProgramInputs()`: a material's props and its React `key` from one
 * merged object. three bakes slot presence and `defines` into the program at
 * first compile, so the key remounts a material whose program inputs or `attach` change.
 * A bare `:NNN` cites three 0.185.1's `WebGLPrograms.js`, where identity is decided.
 */
import * as THREE from 'three';
import { GODOT_ALPHA_HASH } from './materials/godotAlphaHash';
import { alphaAntialiasingOf, coverOneSampleTarget, GODOT_ALPHA_ANTIALIASING, } from './materials/godotAlphaAntialiasing';
import { mipmapBiasInjection, viewportSamplersUserData } from './materials/godotMipmapBias';
import { triplanarInjection } from './materials/godotTriplanar';
import { perObject } from '../utils/perObject';
/**
 * The `> 0` feature booleans (`:140-145`), each its own layer bit: a slider inside
 * the band stays a uniform and a crossing rebuilds, which the element-type switch
 * misses when a second feature holds the upgrade.
 */
const PHYSICAL_FEATURES = [
    'clearcoat',
    'sheen',
    'anisotropy',
    'transmission',
    'iridescence',
    'dispersion',
    // three 0.186.0 added this one: `material.retroreflectivity > 0` at `WebGLPrograms.js:143`,
    // layer bit 24 at `:541-542`.
    'retroreflectivity',
];
/**
 * Every `!! material.<slot>` presence term, keyed as its `…MapUv` term
 * (`:444-466`), with the feature three also requires (`:147-169`) or `null`. One
 * table, so no slot is added without its gate decided. It lists what three bakes,
 * bound here or not, except `bumpMap`: Godot binds no bump slot, and three reads
 * it as `!!bumpMap && wireframe === false` (`:132`), so keying it would key
 * `wireframe` for a slot nothing can fill.
 */
const TEXTURE_SLOT_GATES = {
    map: null,
    alphaMap: null,
    aoMap: null,
    displacementMap: null,
    emissiveMap: null,
    gradientMap: null,
    lightMap: null,
    matcap: null,
    metalnessMap: null,
    normalMap: null,
    roughnessMap: null,
    specularMap: null,
    specularColorMap: null,
    specularIntensityMap: null,
    anisotropyMap: 'anisotropy',
    clearcoatMap: 'clearcoat',
    clearcoatNormalMap: 'clearcoat',
    clearcoatRoughnessMap: 'clearcoat',
    iridescenceMap: 'iridescence',
    iridescenceThicknessMap: 'iridescence',
    sheenColorMap: 'sheen',
    sheenRoughnessMap: 'sheen',
    transmissionMap: 'transmission',
    thicknessMap: 'transmission',
};
/** Iterated per call, so the entry list is built once rather than per key. */
const TEXTURE_SLOT_ENTRIES = Object.entries(TEXTURE_SLOT_GATES);
export function materialProgramInputs(input) {
    const injections = [];
    // Merged here, not at the call site: the last writer wins, and
    // `canvasItemLighting.ts` forces `transparent: true`, so a key from the site's
    // own value would describe a material that does not exist.
    const merged = {};
    // Copied wholesale and stripped once below, rather than rest-destructured per
    // bag: a rest spread rebuilds every key of every part to remove one that most
    // parts do not carry.
    Object.assign(merged, input.props);
    injections.push(input.props.injection);
    for (const bag of input.merge ?? []) {
        if (!bag)
            continue;
        injections.push(bag.injection);
        Object.assign(merged, bag);
    }
    injections.push(...stateInjections(merged));
    if (recordsAlphaAntialiasing(merged.userData))
        merged.onBeforeRender = coverOneSampleTarget;
    const composed = composeInjections(injections);
    if (composed) {
        // `undefined` rather than `delete`: fiber 9.6.1's `applyProps` skips an
        // undefined value (`:24-27`, "Ignore setting undefined props"), and deleting
        // deoptimises the object it is handed.
        merged.injection = undefined;
        merged.customProgramCacheKey = cacheKeyThunk(composed.cacheKey);
        merged.onBeforeCompile = composed.onBeforeCompile;
    }
    // A key rather than `needsUpdate`: `applyProps` never bumps it, and `setProgram`
    // re-derives only on a `version` move or its own re-check list, which a new map
    // or define is not on. A define that stops applying cannot even be cleared,
    // since `applyProps` skips `undefined`. The remount compiles the finished set.
    return {
        key: programKey(merged, composed?.cacheKey ?? ''),
        props: merged,
    };
}
/**
 * `injection` on a material built outside JSX, which `materialProgramInputs` cannot reach, with
 * Godot's hash on a hashed one and its edge ramp on an antialiased one. The cache key is the
 * injections' own, so the patched program never serves an unpatched material.
 */
export function injectProgram(material, injection) {
    if (recordsAlphaAntialiasing(material.userData))
        material.onBeforeRender = coverOneSampleTarget;
    const composed = composeInjections([injection, ...stateInjections(material)]);
    if (!composed)
        return;
    material.onBeforeCompile = composed.onBeforeCompile;
    material.customProgramCacheKey = cacheKeyThunk(composed.cacheKey);
}
/**
 * `injectProgram` for a 3D material that is not derived from a StandardMaterial3D bag: Godot reads
 * its textures through the viewport's samplers, so it carries the mipmap bias too.
 */
export function injectViewportProgram(material, injection) {
    Object.assign(material.userData, viewportSamplersUserData());
    injectProgram(material, injection);
}
/** `materialProgramInputs` for a 3D material outside a StandardMaterial3D bag, as `injectViewportProgram`. */
export function viewportProgramInputs(input) {
    const userData = { ...input.props.userData, ...viewportSamplersUserData() };
    return materialProgramInputs({ ...input, props: { ...input.props, userData } });
}
/**
 * The injections a material's own state turns on: Godot's hash, its edge ramp, its triplanar
 * mapping, then the mipmap bias. The triplanar mapping and the bias come last, as each inlines the
 * chunks that sample a texture, which the patches before them match by their `#include`. The bias
 * then finds each projection's sample.
 */
function stateInjections(state) {
    const injections = [];
    if (state.alphaHash === true)
        injections.push(GODOT_ALPHA_HASH);
    if (recordsAlphaAntialiasing(state.userData))
        injections.push(GODOT_ALPHA_ANTIALIASING);
    const triplanar = triplanarInjection(state);
    if (triplanar)
        injections.push(triplanar);
    const bias = mipmapBiasInjection(state);
    if (bias)
        injections.push(bias);
    return injections;
}
/** The cache key of the injections a material's own state turns on, empty for none. */
export function stateInjectionKey(material) {
    return composeInjections(stateInjections(material))?.cacheKey ?? '';
}
/** Whether a material's `userData` records the alpha antialiasing whose edge ramp it compiles with. */
function recordsAlphaAntialiasing(userData) {
    return typeof userData === 'object' && userData !== null && alphaAntialiasingOf({ userData }) !== null;
}
/** The injection that defines `define` in the fragment shader, its cache key the define's name. */
export function defining(define) {
    return {
        cacheKey: define.toLowerCase(),
        onBeforeCompile(shader) {
            shader.fragmentShader = `#define ${define}\n${shader.fragmentShader}`;
        },
    };
}
/**
 * One injection that applies each present part in order, or none when no part is present. One
 * part passes straight through, so `useCanvasItemLighting`'s memo still reaches the material.
 */
export function composeInjections(parts) {
    let composed;
    for (const part of parts) {
        if (part)
            composed = composed ? pairsWith(composed)(part) : part;
    }
    return composed;
}
/**
 * Each pair composed so far, keyed by its parts' identity rather than their cache keys, since
 * `useCanvasItemLighting` gives each instance its own patch under one key. Stable parts then
 * compose to a stable injection on every render. Collected with the first part.
 */
const pairsWith = perObject((first) => perObject((second) => pairOf(first, second)));
function pairOf(first, second) {
    // A method, so each part reads the material three compiles.
    return {
        cacheKey: `${first.cacheKey}+${second.cacheKey}`,
        onBeforeCompile(shader) {
            first.onBeforeCompile.call(this, shader);
            second.onBeforeCompile.call(this, shader);
        },
    };
}
/**
 * One thunk per composed contribution, a handful of module literals. A fresh
 * closure per render recompiles nothing, since `applyProps` never bumps
 * `needsUpdate`, but it would overwrite the stable function `useCanvasItemLighting`
 * memoises.
 */
const CACHE_KEY_THUNKS = new Map();
function cacheKeyThunk(cacheKey) {
    const existing = CACHE_KEY_THUNKS.get(cacheKey);
    if (existing)
        return existing;
    // A method, not an arrow, since it reads the material three calls it on. This own property
    // shadows the prototype key, so it appends that key.
    const thunk = function () {
        return cacheKey + THREE.Material.prototype.customProgramCacheKey.call(this);
    };
    CACHE_KEY_THUNKS.set(cacheKey, thunk);
    return thunk;
}
/**
 * Composed strings only: a patch's function identity never enters the key. Not
 * keyed, as `setProgram` re-checks them: `clippingPlanes` (`:354`, `WebGLRenderer.js:2456-2458`),
 * and `envMap`, `vertexAlphas`, `vertexTangents`, scene `fog` and tone mapping
 * (`WebGLRenderer.js:2390-2494`).
 */
function programKey(props, cacheKey) {
    let key = '';
    /**
     * Every term is labelled and `|`-separated, so an absent one reads "at its
     * default" while no `defines` value or `cacheKey` holds a `|`. An empty key is
     * fine: it separates a material from its own past self, not from its siblings.
     */
    const add = (term) => {
        key = key === '' ? term : `${key}|${term}`;
    };
    const active = (name) => Number(props[name] ?? 0) > 0;
    const alphaToCoverage = props.alphaToCoverage === true;
    const blending = props.blending ?? THREE.NormalBlending;
    // The `opaque` composite (`:262`, one boolean at `:581`), as its negation so the
    // opaque default costs no token. `blending` enters only here, so on a transparent
    // material it is per-draw GL state. `alphaToCoverage` is also its own term (`:213`,
    // layer `:589`).
    if (props.transparent === true || blending !== THREE.NormalBlending || alphaToCoverage)
        add('blended');
    if (alphaToCoverage)
        add('a2c');
    // `alphaHash` (`:172`, published `:266`) is a plain field (`Material.js:134`).
    // `alphaTest` (`:170`) is not keyed: its `> 0` crossing bumps `version` itself
    // (`Material.js:494-502`).
    if (props.alphaHash === true)
        add('ahash');
    // `side` (`:369-370`) has one default across material types, so it is normalised.
    // `combine` (`:268`) and `fog` (`:314`) do not. Also keyed: `vertexColors` (`:308`),
    // `premultipliedAlpha` (`:367`), `dithering` (`:357`).
    const side = props.side ?? THREE.FrontSide;
    if (side !== THREE.FrontSide)
        add(`side:${String(side)}`);
    if (props.vertexColors === true)
        add('vcol');
    if (props.premultipliedAlpha === true)
        add('premul');
    if (props.combine !== undefined)
        add(`combine:${String(props.combine)}`);
    if (props.dithering === true)
        add('dither');
    if (props.fog !== undefined)
        add(`fog:${String(props.fog)}`);
    // `:317`, first arm only: the second reads geometry and the material's brand,
    // out of reach like `vertexAlphas`. `wireframe` alone reaches only this and the
    // `bumpMap` term (`:132`), a slot this factory declines.
    if (props.flatShading === true && props.wireframe !== true)
        add('flat');
    let features = '';
    for (const feature of PHYSICAL_FEATURES)
        if (active(feature))
            features += features === '' ? feature : `+${feature}`;
    if (features !== '')
        add(`feat:${features}`);
    // Presence only: a swapped texture compiles to the same program. The one
    // identity-dependent term, `decodeVideoTexture` (`:364`), cannot fire: no map is a
    // `THREE.VideoTexture`, and `canvas2DTextureDecode.ts` retags canvas maps `NoColorSpace`.
    let slots = '';
    for (const [slot, gate] of TEXTURE_SLOT_ENTRIES) {
        if (!props[slot])
            continue;
        if (gate !== null && !active(gate))
            continue;
        slots += slots === '' ? slot : `+${slot}`;
    }
    if (slots !== '')
        add(`slots:${slots}`);
    // Keys and values both (`:415-420`).
    const defines = props.defines;
    if (defines !== undefined && defines !== null) {
        let declared = '';
        for (const name of Object.keys(defines).sort())
            declared += `${declared === '' ? '' : '+'}${name}=${defines[name]}`;
        if (declared !== '')
            add(`defines:${declared}`);
    }
    // The injections' part of `customProgramCacheKey()` (`:382`, pushed at `:432`). Its curve term
    // stays out: a curve swap marks the material dirty, which compiles it without a remount.
    if (cacheKey !== '')
        add(`inject:${cacheKey}`);
    // Not a program input, but mount-only the same way: fiber applies `attach` only at
    // mount (`RESERVED_PROPS` in `@react-three/fiber` 9.8.0), so a moved target remounts.
    if (typeof props.attach === 'string')
        add(`attach:${props.attach}`);
    return key;
}
//# sourceMappingURL=materialProgramInputs.js.map